/**
 * «Настройки → Полномочия»: кто что может в prokat.
 *
 * Спека: `docs/specs/2026-09-17-permissions-profit-design.md`, раздел «Экран».
 * Люди — те, кто входил, и держатели прав: новый сотрудник появится после
 * первого входа, прав по умолчанию у него нет.
 */
import { keys, useGetIdentity } from "@refinedev/core";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { NoAccess } from "@/components/NoAccess";
import { PERMISSIONS_EDIT, useCan } from "@/lib/access";
import { mskTime } from "@/lib/dates";
import { loadPermissions, type PermissionsAnswer, setPermission } from "@/lib/permissions";
import type { CurrentUser } from "@/lib/providers";

export function PermissionsPage() {
  return useCan(PERMISSIONS_EDIT) ? <PermissionsScreen /> : <NoAccess what="полномочиям" />;
}

function PermissionsScreen() {
  const [answer, setAnswer] = useState<PermissionsAnswer | null>(null);
  const [failure, setFailure] = useState("");
  // Ключ — id сотрудника, не «сотрудник:код»: ответ несёт ВЕСЬ список кодов
  // сотрудника, поэтому два запроса по одной строке (разные права) гонятся
  // за одно и то же поле `codes`, и более старый ответ может затереть более
  // свежий. Пока запрос по сотруднику в пути, блокируем ВСЮ его строку.
  const [busy, setBusy] = useState<Set<number>>(() => new Set());
  const { data: me } = useGetIdentity<CurrentUser>();
  const queryClient = useQueryClient();

  useEffect(() => {
    let alive = true;
    loadPermissions()
      .then((got) => {
        if (alive) setAnswer(got);
      })
      .catch((error: Error) => {
        if (alive) setFailure(error.message);
      });
    return () => {
      alive = false;
    };
  }, []);

  const toggle = (userId: number, code: string, on: boolean) => {
    setFailure("");
    setBusy((was) => new Set(was).add(userId));
    setPermission(userId, code, on)
      .then((got) => {
        setAnswer((was) =>
          was ? { ...was, users: was.users.map((u) => (u.id === got.user_id ? { ...u, codes: got.codes } : u)) } : was,
        );
        // Себе поменял права — меню и экраны кабинета читают личность из
        // кэша Refine (`/api/me`) и без сброса остались бы со старым набором
        // до перезагрузки страницы. Сбрасываем ТОЛЬКО `identity`: каркас
        // (`App.tsx`) держит весь кабинет под `<Authenticated>` без `loading`,
        // и её собственный запрос `check` на время рефетча гасит экран целиком
        // (`isFetching` → `null`), а не только личность — этот экран тоже.
        if (me && got.user_id === me.id) {
          void queryClient.invalidateQueries({ queryKey: keys().auth().action("identity").get() });
        }
      })
      // Отказ (последний держатель, нет права) — словами сервера; галочка
      // остаётся как была, потому что состояние меняет только ответ.
      .catch((error: Error) => setFailure(error.message))
      .finally(() =>
        setBusy((was) => {
          const next = new Set(was);
          next.delete(userId);
          return next;
        }),
      );
  };

  return (
    <main className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 [&>*]:flex-none">
      <div className="text-[12.5px] text-fg-muted">
        Права на разделы prokat. Сотрудник появляется здесь после первого входа; прав по умолчанию у него нет.
      </div>
      {answer === null && !failure ? <div className="text-[12.5px] text-fg-muted">…</div> : null}
      {answer ? (
        <div className="overflow-x-auto rounded-[var(--radius-card)] border border-border" data-annot="полномочия">
          <table className="w-max min-w-full border-collapse text-[12.5px]">
            <thead>
              <tr className="bg-bg-subtle text-[11px] font-medium text-fg-muted">
                <th className="border-b border-border px-3 py-2 text-left">сотрудник</th>
                <th className="border-b border-border px-3 py-2 text-left">последний вход</th>
                {answer.permissions.map((p) => (
                  <th key={p.code} className="border-b border-border px-3 py-2 text-center" title={p.hint}>
                    {p.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {answer.users.map((u) => (
                <tr key={u.id} className="hover:bg-bg-hover" data-annot={`сотрудник ${u.id}`}>
                  <td className="border-b border-border px-3 py-2">
                    {u.name ?? <span className="text-fg-faint">не входил</span>}
                    <span className="ml-2 font-mono text-[11px] text-fg-faint">{u.id}</span>
                  </td>
                  <td className="border-b border-border px-3 py-2 tabular-nums text-fg-muted">
                    {mskTime(u.last_login)}
                  </td>
                  {answer.permissions.map((p) => {
                    const on = u.codes.includes(p.code);
                    return (
                      <td key={p.code} className="border-b border-border px-3 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={on}
                          disabled={busy.has(u.id)}
                          onChange={() => toggle(u.id, p.code, !on)}
                          aria-label={`${p.label}: ${u.name ?? u.id}`}
                          className="size-4 cursor-pointer disabled:cursor-wait disabled:opacity-40"
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {failure ? <div className="text-[12.5px] text-warn">{failure}</div> : null}
    </main>
  );
}
